import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { AlertTriangle, BadgeCheck, BookOpen, CreditCard, Bot, Building2, GitBranch, KeyRound, LayoutGrid, LogOut, Menu, Moon, Search, ScrollText, Settings, Ship, Smartphone, Sun, Tag, Users, X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type * as S from "@shipyard/shared";
import { LanguageMenu } from "@/components/language-menu";
import { http } from "@/lib/api";
import { useApps, useEntitlements, useLogout, useOrgs, useSwitchOrg } from "@/lib/queries";
import { isInstanceAdmin, isOrgAdmin } from "@/lib/roles";
import { cn } from "@/lib/utils";
import { CommandPalette, SHORTCUT_LABEL, useCommandPaletteShortcut } from "./command-palette";
import { PlatformIcon, toastError } from "./common";
import { Select } from "./ui";

const THEME_KEY = "shipyard.theme";

function useTheme() {
  const [dark, setDark] = useState(() => {
    try {
      const saved = localStorage.getItem(THEME_KEY);
      if (saved) return saved === "dark";
    } catch {
      // ignore
    }
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    try {
      localStorage.setItem(THEME_KEY, dark ? "dark" : "light");
    } catch {
      // ignore
    }
  }, [dark]);
  return [dark, () => setDark((d) => !d)] as const;
}

function NavItem({ to, icon, children, exact }: { to: string; icon: ReactNode; children: ReactNode; exact?: boolean }) {
  return (
    <Link
      to={to}
      activeOptions={{ exact }}
      className="flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-fg-muted hover:bg-muted hover:text-fg"
      activeProps={{ className: "!bg-muted !text-fg font-medium" }}
    >
      <span className="shrink-0">{icon}</span>
      <span className="truncate">{children}</span>
    </Link>
  );
}

export function Logo() {
  return (
    <div className="flex items-center gap-2 font-semibold tracking-tight">
      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-900 text-sky-400 dark:bg-sky-500 dark:text-slate-950">
        <Ship className="h-4 w-4" />
      </span>
      Shipyard
    </div>
  );
}

function SearchButton({ onClick, className }: { onClick: () => void; className?: string }) {
  const { t } = useTranslation();
  return (
    <button
      onClick={onClick}
      className={cn("flex w-full items-center gap-2 rounded-md border bg-bg px-2.5 py-1.5 text-sm text-fg-muted transition-colors hover:border-brand-500/60 hover:text-fg", className)}
    >
      <Search className="h-4 w-4 shrink-0" />
      <span className="flex-1 truncate text-left">{t("search.trigger")}</span>
      <kbd className="hidden rounded border bg-panel px-1.5 font-sans text-[10px] font-medium md:inline">{SHORTCUT_LABEL}</kbd>
    </button>
  );
}

/** Warns instance admins about an expiring or invalid license, or when seats exceed the license. */
function LicenseBanner() {
  const { t, i18n } = useTranslation();
  const entitlements = useEntitlements();
  const data = entitlements.data;
  const license = data?.license;
  if (!data || !license) return null;
  let message: string | null = null;
  if (license.status === "grace") message = t("license.bannerGrace", { date: new Date(license.graceEndsAt!).toLocaleDateString(i18n.language, { timeZone: "UTC", dateStyle: "long" }) });
  else if (license.status === "expired") message = t("license.bannerExpired");
  else if (license.status === "invalid") message = t("license.bannerInvalid");
  else if (data.seats.limit !== null && data.seats.used > data.seats.limit) message = t("license.bannerSeats", { used: data.seats.used, limit: data.seats.limit });
  if (!message) return null;
  return (
    <Link to="/admin/license" className="flex items-center gap-2 border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
      <AlertTriangle className="h-4 w-4 shrink-0" />
      {message}
    </Link>
  );
}

/** Active organization, with a switcher when the user belongs to several. */
function OrgSwitcher({ me }: { me: S.Me }) {
  const { t } = useTranslation();
  const orgs = useOrgs();
  const switchOrg = useSwitchOrg();
  const navigate = useNavigate();
  if (!me.organization) return null;
  if (!orgs.data || orgs.data.length < 2) {
    return (
      <p className="flex items-center gap-2 truncate text-sm text-fg-muted" title={me.organization.name}>
        <Building2 className="h-4 w-4 shrink-0" />
        <span className="truncate">{me.organization.name}</span>
      </p>
    );
  }
  return (
    <Select
      aria-label={t("nav.organization")}
      className="h-8 text-sm"
      value={me.organization.id}
      disabled={switchOrg.isPending}
      onChange={(e) =>
        switchOrg.mutate(e.target.value, {
          onSuccess: () => void navigate({ to: "/apps" }),
          onError: toastError,
        })
      }
    >
      {orgs.data.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </Select>
  );
}

function AppLinks({ apps }: { apps: S.App[] | undefined }) {
  if (!apps?.length) return null;
  return (
    <div className="mt-1 space-y-0.5 pl-2">
      {apps.map((app) => (
        <Link
          key={app.id}
          to="/apps/$appId"
          params={{ appId: app.id }}
          className="flex items-center gap-2 rounded-md px-2.5 py-1 text-sm text-fg-muted hover:bg-muted hover:text-fg"
          activeProps={{ className: "!text-fg font-medium" }}
        >
          <span className="flex gap-0.5 opacity-70">
            {app.iosBundleId && <PlatformIcon platform="ios" className="h-3 w-3" />}
            {app.androidPackage && <PlatformIcon platform="android" className="h-3 w-3" />}
          </span>
          <span className="truncate">{app.name}</span>
        </Link>
      ))}
    </div>
  );
}

/** `children` replaces the routed page (e.g. when the user has no organization). */
export function AppLayout({ me, children }: { me: S.Me; children?: ReactNode }) {
  const { t, i18n } = useTranslation();
  const [dark, toggleTheme] = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  useCommandPaletteShortcut(setPaletteOpen);
  const apps = useApps();
  const logout = useLogout();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => setMobileOpen(false), [pathname]);
  const isAdmin = isOrgAdmin(me);
  // Organization admins: the Billing entry depends on the deployment.
  const entitlements = useEntitlements(isAdmin);

  const sidebar = (
    <nav className="flex h-full flex-col gap-6 overflow-y-auto px-3 py-4">
      <div className="space-y-4 px-2">
        <Logo />
        <OrgSwitcher me={me} />
        <SearchButton onClick={() => setPaletteOpen(true)} />
      </div>
      <div className="space-y-0.5">
        <NavItem to="/apps" icon={<LayoutGrid className="h-4 w-4" />} exact>
          {t("nav.apps")}
        </NavItem>
        <AppLinks apps={apps.data} />
      </div>
      {isAdmin && (
        <div className="space-y-0.5">
          <p className="px-2.5 pb-1 text-xs font-medium uppercase tracking-wide text-fg-muted">{t("nav.administration")}</p>
          <NavItem to="/organization" icon={<Building2 className="h-4 w-4" />} exact>
            {t("nav.organization")}
          </NavItem>
          <NavItem to="/organization/audit" icon={<ScrollText className="h-4 w-4" />}>
            {t("nav.audit")}
          </NavItem>
          {entitlements.data?.deployment === "cloud" && (
            <NavItem to="/organization/billing" icon={<CreditCard className="h-4 w-4" />}>
              {t("nav.billing")}
            </NavItem>
          )}
          <NavItem to="/admin/credentials" icon={<KeyRound className="h-4 w-4" />}>
            {t("nav.credentials")}
          </NavItem>
          <NavItem to="/admin/llm" icon={<Bot className="h-4 w-4" />}>
            {t("nav.llm")}
          </NavItem>
          <NavItem to="/admin/git" icon={<GitBranch className="h-4 w-4" />}>
            {t("nav.git")}
          </NavItem>
          <NavItem to="/admin/tags" icon={<Tag className="h-4 w-4" />}>
            {t("nav.tags")}
          </NavItem>
          <NavItem to="/admin/devices" icon={<Smartphone className="h-4 w-4" />}>
            {t("nav.devices")}
          </NavItem>
        </div>
      )}
      {isInstanceAdmin(me) && (
        <div className="space-y-0.5">
          <p className="px-2.5 pb-1 text-xs font-medium uppercase tracking-wide text-fg-muted">{t("nav.instance")}</p>
          <NavItem to="/admin/users" icon={<Users className="h-4 w-4" />}>
            {t("nav.users")}
          </NavItem>
          <NavItem to="/admin/license" icon={<BadgeCheck className="h-4 w-4" />}>
            {t("nav.license")}
          </NavItem>
        </div>
      )}
      <div className="mt-auto space-y-0.5 border-t pt-4">
        <NavItem to="/settings" icon={<Settings className="h-4 w-4" />}>
          {t("nav.settings")}
        </NavItem>
        <a href="/docs" target="_blank" rel="noreferrer" className="flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-fg-muted hover:bg-muted hover:text-fg">
          <BookOpen className="h-4 w-4" />
          {t("nav.apiDocs")}
        </a>
        <div className="flex items-center gap-1 px-1 pt-2">
          <button className="rounded-md p-1.5 text-fg-muted hover:bg-muted hover:text-fg" onClick={toggleTheme} title={t("nav.theme")} aria-label={t("nav.theme")}>
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <LanguageMenu side="top" align="start" onChange={(locale) => void http.patch("/me", { locale }).catch(() => undefined)} />
          <div className="ml-auto flex min-w-0 items-center gap-2">
            <span className="truncate text-xs text-fg-muted" title={me.email}>
              {me.name}
            </span>
            <button className="rounded-md p-1.5 text-fg-muted hover:bg-muted hover:text-fg" onClick={() => logout.mutate()} title={t("auth.logout")} aria-label={t("auth.logout")}>
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </nav>
  );

  return (
    <div className="flex h-full">
      <aside className="hidden w-60 shrink-0 border-r bg-panel md:block">{sidebar}</aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-30 md:hidden">
          <div className="absolute inset-0 bg-slate-950/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 border-r bg-panel">{sidebar}</aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b bg-panel px-4 py-3 md:hidden">
          <Logo />
          <div className="flex items-center gap-1">
            <button className="rounded-md p-1.5 hover:bg-muted" onClick={() => setPaletteOpen(true)} aria-label={t("search.title")}>
              <Search className="h-5 w-5" />
            </button>
            <button className="rounded-md p-1.5 hover:bg-muted" onClick={() => setMobileOpen((o) => !o)} aria-label="Menu">
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </header>
        {isInstanceAdmin(me) && <LicenseBanner />}
        <main className={cn("min-w-0 flex-1 overflow-y-auto")}>
          <div className="mx-auto max-w-6xl px-4 py-6 md:px-8 md:py-8">
            {children ?? <Outlet />}
          </div>
        </main>
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} me={me} dark={dark} toggleTheme={toggleTheme} />
    </div>
  );
}
